import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/components/ui-v2/sidebar';
import { ProductType } from '@/lib/graphql/generated/graphql';
import {
  ColumnFilterId,
  columnFiltersState,
} from '@/states/column-filters.state';
import {
  DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES,
  filterSidebarAccordionDefaultValueState,
} from '@/states/filter-sidebar-accordion.state';
import { hasDigitalProductsWithoutCouponCodesAtom } from '@/states/has-digital-products-without-coupon-codes.state';
import { openImportCouponCodesModalAtom } from '@/states/is-sidebar-alert-open.state';
import { NO_CATEGORY_FILTER_KEY } from '@/states/product-category-filter.state';
import { productsState } from '@/states/products.state';
import { scrollToTableColumnIdState } from '@/states/scroll-to-table-column.state';
import { vatRatesAtom } from '@/states/vat-rates.state';
import { useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle } from 'lucide-react';

import { Button } from './ui/button';

interface AlertSidebarProps {
  onClose: () => void;
}

export const AlertSidebar: React.FC<AlertSidebarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { uniqueVatRates } = useAtomValue(vatRatesAtom);
  const hasDigitalProductsWithoutCoupons = useAtomValue(
    hasDigitalProductsWithoutCouponCodesAtom
  );
  const products = useAtomValue(productsState);

  const setColumnFilters = useSetAtom(columnFiltersState);
  const setFilterSidebarAccordionDefault = useSetAtom(
    filterSidebarAccordionDefaultValueState
  );
  const setScrollToColumnId = useSetAtom(scrollToTableColumnIdState);

  const handleVatRateCheck = useCallback(
    (rate: number) => {
      const categoryIds = new Set<string>();
      let includeNoCategory = false;
      for (const product of products) {
        if (
          !product.activeVersion.isSelected ||
          product.activeVersion.vatRate !== rate
        )
          continue;
        const id = product.activeVersion.productCategory?.productCategoryId;
        if (id) categoryIds.add(id);
        else includeNoCategory = true;
      }
      const nextValue = [...categoryIds].sort((a, b) => a.localeCompare(b));
      if (includeNoCategory) nextValue.push(NO_CATEGORY_FILTER_KEY);

      setColumnFilters((previous) => {
        const hasEntry = previous.some(
          (f) => f.id === ColumnFilterId.productCategory
        );
        if (hasEntry) {
          return previous.map((f) =>
            f.id === ColumnFilterId.productCategory
              ? { ...f, value: nextValue }
              : f
          );
        }
        return [
          ...previous,
          { id: ColumnFilterId.productCategory, value: nextValue },
        ];
      });

      setFilterSidebarAccordionDefault([
        ...DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES,
        'categories',
      ]);
      setScrollToColumnId('vatRate');
      onClose();
    },
    [
      products,
      setColumnFilters,
      setFilterSidebarAccordionDefault,
      onClose,
      setScrollToColumnId,
    ]
  );

  const countProductsByVatRate = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const product of products) {
      const vatRate = product.activeVersion.vatRate;
      if (vatRate != null) {
        counts[vatRate] = (counts[vatRate] ?? 0) + 1;
      }
    }
    return counts;
  }, [products]);

  const digitalProductsCount = useMemo(() => {
    let count = 0;
    for (const product of products) {
      if (
        product.activeVersion.isSelected &&
        product.activeVersion.productType === ProductType.Coupon
      ) {
        count += 1;
      }
    }
    return count;
  }, [products]);

  const setOpenImportCouponCodesModal = useSetAtom(
    openImportCouponCodesModalAtom
  );

  return (
    <Sidebar
      title={t('components.alert_sidebar.title')}
      onClose={onClose}
      showOverlay
    >
      <div className="space-y-4">
        {/* Missing Coupon Codes Section */}
        {hasDigitalProductsWithoutCoupons && (
          <div className="border-b p-2 pb-6">
            <div className="flex items-center gap-2 pb-4">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <p className="text-md text-destructive">
                {t('components.alert_sidebar.missing_coupons_title')}
              </p>
            </div>

            <div className="flex w-full items-center gap-2 rounded-md border p-2">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-gray-400">
                  {t(
                    'components.alert_sidebar.missing_coupons_products_count',
                    {
                      count: digitalProductsCount,
                    }
                  )}
                </p>
                <span className="text-md">{digitalProductsCount}</span>
              </div>
              <Button
                type="button"
                variant="secondary"
                className="ml-auto font-semibold"
                data-testid="alert-sidebar-import-coupon-codes"
                onClick={() => {
                  setOpenImportCouponCodesModal(true);
                  onClose();
                }}
              >
                <span> {t('general.import')}</span>
              </Button>
            </div>
          </div>
        )}

        {/* VAT Alerts Section */}
        {uniqueVatRates.length > 1 && (
          <div className="p-2 pb-6">
            <div className="flex flex-col gap-2 pb-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                <p className="text-md text-orange-500">
                  {t('components.alert_sidebar.vat_rates_detected', {
                    count: uniqueVatRates.length,
                  })}
                </p>
              </div>
              <p className="text-sm text-gray-400">
                {t('components.alert_sidebar.vat_rates_description', {
                  count: uniqueVatRates.length,
                  vatRates: uniqueVatRates.join(', '),
                })}
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex w-full flex-col gap-6">
                {uniqueVatRates.map((rate) => (
                  <div
                    key={rate}
                    className="flex w-full items-center gap-2 rounded-md border p-2"
                  >
                    <div className="flex flex-col gap-2 px-2">
                      <span className="text-sm text-gray-400">
                        {t(
                          'components.alert_sidebar.vat_rates_products_count',
                          {
                            vatRate: rate,
                          }
                        )}
                      </span>
                      <span className="text-md">
                        {countProductsByVatRate[rate]}
                      </span>
                    </div>
                    <Button
                      variant="secondary"
                      className="ml-auto font-semibold"
                      data-testid={`alert-sidebar-vat-rate-check-${rate}`}
                      onClick={() => handleVatRateCheck(rate)}
                    >
                      {t('components.alert_sidebar.vat_rates_check')}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};
